import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Zap,
  BrainCircuit,
  LineChart,
  Smartphone,
  Mail,
  Calendar,
  Users,
  TrendingUp,
  ArrowRight,
} from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import heroAsset from "@/assets/solutions/sales/hero.webp.asset.json";
import captureAsset from "@/assets/solutions/sales/capture.webp.asset.json";
import followAsset from "@/assets/solutions/sales/follow.webp.asset.json";
import pipelineAsset from "@/assets/solutions/sales/pipeline.webp.asset.json";

const hero = heroAsset.url;
const capture = captureAsset.url;
const follow = followAsset.url;
const pipeline = pipelineAsset.url;

const heroAlt =
  "Sales rep sharing a Banana NFC business card with a prospect who instantly receives the digital contact profile on their phone";
const captureAlt =
  "Field sales rep tapping a branded Banana NFC business card to a client's phone, capturing the lead into the CRM in seconds";
const followAlt =
  "Banana AI sales assistant dashboard drafting personalized follow-up emails and surfacing recent contacts from the CRM";
const pipelineAlt =
  "Banana CRM sales pipeline board showing deals moving through New Leads, Contacted, In Meeting and Demo stages with contact tagging on mobile";

export const Route = createFileRoute("/solutions/sales")({
  head: () => ({
    meta: [
      { title: "CRM & NFC cards for Sales Teams — banana.cards" },
      {
        name: "description",
        content:
          "Capture leads with a tap, let AI draft the follow-up, and keep your pipeline honest. Banana is the all-in-one sales stack for modern reps.",
      },
      { property: "og:title", content: "CRM & NFC cards for Sales Teams — banana.cards" },
      {
        property: "og:description",
        content:
          "Capture leads with a tap, let AI draft the follow-up, and keep your pipeline honest.",
      },
      { property: "og:image", content: hero },
      { property: "og:url", content: "https://banana.cards/solutions/sales" },
    ],
    links: [{ rel: "canonical", href: "https://banana.cards/solutions/sales" }],
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="For Sales Teams"
      title="Close more, type"
      italic="less."
      description="Banana captures every conversation, drafts the follow-up, and keeps your pipeline in sync — so your reps spend their day selling, not updating CRM fields."
      heroImage={hero}
      heroImageAlt={heroAlt}
      metrics={[
        { v: "3×", l: "Faster follow-up" },
        { v: "+38%", l: "Reply rate vs. cold templates" },
        { v: "12h", l: "Saved per rep, per week" },
      ]}
      bullets={[
        {
          icon: Zap,
          title: "Lead capture in 3 seconds",
          desc: "Tap, scan a card or drop a business card photo. Banana parses the contact, enriches it with LinkedIn and company data, and adds it to your pipeline.",
        },
        {
          icon: BrainCircuit,
          title: "AI follow-ups that sound like you",
          desc: "Trained on your meeting notes, voice and product. Drafts land in your inbox within minutes of every call.",
        },
        {
          icon: LineChart,
          title: "Pipeline that updates itself",
          desc: "Meetings move stages automatically. Tasks get created from action items. Forecasts roll up to the manager view without a single manual edit.",
        },
      ]}
      deepDives={[
        {
          eyebrow: "Capture",
          title: "Turn every handshake",
          italic: "into a record.",
          description:
            "Tap your NFC card at a booth, scan a paper card in the back of an Uber, or forward a meeting invite — Banana captures the lead and routes it to the right rep, pipeline and stage.",
          points: [
            "NFC tap from any iPhone or Android",
            "AI business-card scanner (OCR + enrichment)",
            "Round-robin assignment & territory routing",
            "Auto-dedupes against existing accounts",
          ],
          image: capture,
          imageAlt: captureAlt,
        },
        {
          eyebrow: "Follow up",
          title: "AI does the typing,",
          italic: "you do the closing.",
          description:
            "After every meeting, Banana writes a personalized recap, suggests next steps, and queues a follow-up sequence that adapts based on whether the prospect opens, clicks or books.",
          points: [
            "Recap emails generated from meeting transcripts",
            "Sequences that branch on engagement",
            "A/B subject lines tested per persona",
            "One-click send from inbox or CRM",
          ],
          image: follow,
          imageAlt: followAlt,
          reverse: true,
        },
        {
          eyebrow: "Forecast",
          title: "A pipeline you can",
          italic: "actually trust.",
          description:
            "Stages move forward when real signals happen — emails replied, meetings booked, contracts viewed. No more end-of-quarter scramble to update CRM fields nobody believes.",
          points: [
            "Auto-stage progression from real activity",
            "Multi-currency weighted forecasting",
            "Slip & risk alerts for stuck deals",
            "Manager dashboards & rep scorecards",
          ],
          image: pipeline,
          imageAlt: pipelineAlt,
        },
      ]}
      howItWorks={[
        { title: "Ship cards to your team", desc: "Branded NFC cards delivered worldwide in 5–7 days. No app required for the recipient." },
        { title: "Connect your inbox & calendar", desc: "Gmail, Outlook, Google & iCloud sync in two clicks. Existing CRM data imports in minutes." },
        { title: "Capture & let AI work", desc: "Reps tap. Meetings get noted. Drafts appear in the outbox. Pipeline moves on its own." },
        { title: "Coach with real data", desc: "Managers see who's following up, who isn't, and where deals are slipping — in real time." },
      ]}
      capabilities={[
        "Branded NFC business cards",
        "AI meeting notetaker (Zoom/Meet/Teams)",
        "Auto-generated follow-up emails",
        "Pipeline with weighted forecasting",
        "Round-robin lead assignment",
        "Salesforce & HubSpot sync",
        "Team analytics & rep scorecards",
        "SSO, SCIM & role-based access",
      ]}
      faq={[
        {
          q: "How does Banana actually capture leads at events?",
          a: "Reps tap their NFC business card on any phone — no app required. The prospect lands on a branded profile and can save the contact or share theirs back. If they share, the lead lands in your pipeline with their name, title, company, LinkedIn and any custom-form answers. For paper cards, the AI scanner extracts the same fields from a photo in seconds.",
        },
        {
          q: "What does the AI follow-up actually write?",
          a: "After a meeting, the AI summarizes what was discussed, the prospect's stated pain points, and the agreed next steps. It then drafts a personalized recap email in your voice and queues a 5-touch sequence tailored to the deal stage. You can edit anything before it goes out, or let it auto-send on a delay.",
        },
        {
          q: "Does it sync with Salesforce or HubSpot?",
          a: "Yes — two-way sync with Salesforce, HubSpot and Pipedrive. Contacts, accounts, opportunities and activities map both ways. You can keep your existing CRM as the source of truth and use Banana as the capture + AI layer, or run it standalone.",
        },
        {
          q: "Can managers see what each rep is doing?",
          a: "Yes. The manager dashboard shows every meeting, follow-up, and stage change per rep, with team-level forecasting and pipeline health metrics. Coaching snippets surface deals that haven't been touched in 7+ days or are missing required next steps.",
        },
        {
          q: "How fast can a team of 50 get started?",
          a: "Cards ship in under a week. Setup — inbox, calendar, CRM sync, SSO — takes about an hour for an admin. Reps are typically live and capturing leads the same day they get their card. Most teams see their first AI-drafted follow-up within 24 hours.",
        },
      ]}
      outro={{
        title: "Give your reps back",
        italic: "their week.",
        description:
          "Stop paying senior closers to be data-entry clerks. Banana captures, drafts and updates — so your team can sell.",
      }}
      afterFaq={<UseCases tag="Sales" />}
    />
  );
}

function UseCases({ tag }: { tag: string }) {
  const items = [
    { t: "Field sales & territory reps", d: "Tap-to-share at site visits, instant CRM entry, drive-time-aware follow-ups.", to: "/platform/scout" },
    { t: "Inside sales & SDRs", d: "Connect inbox + calendar, let AI draft personalized openers and book meetings.", to: "/ai/email-writer" },
    { t: "Event & conference teams", d: "Capture hundreds of badges in a day, route by territory, follow up before the flight home.", to: "/platform/events" },
    { t: "Account executives", d: "Auto-recap discovery calls, generate proposals & contracts, forecast with confidence.", to: "/platform/contracts" },
  ];
  return (
    <section className="bg-background">
      <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{tag} use cases</p>
        <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
          Every role on your team, <em className="italic">covered.</em>
        </h2>
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {items.map((i) => (
            <Link key={i.t} to={i.to} className="group rounded-2xl bg-muted p-6 transition hover:bg-muted/70">
              <h3 className="text-lg font-bold">{i.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{i.d}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 group-hover:underline">
                Explore <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
